import React, { useState, useEffect, useCallback } from 'react';
import hiveService from '../../services/hive';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import './WalletInfo.css';

const WalletInfo = ({ username, onClose }) => {
  const [walletData, setWalletData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTotals, setShowTotals] = useState(false);
  const [totalsData, setTotalsData] = useState(null);
  const [error, setError] = useState('');
  const [includeDelegated, setIncludeDelegated] = useState(false);
  const [showTickerInput, setShowTickerInput] = useState(false);
  const [tickerInput, setTickerInput] = useState('');
  const [inputError, setInputError] = useState('');
  const [showHiveRaw, setShowHiveRaw] = useState(false);
  const showEngineRaw = false;
  const [engineAllTokens, setEngineAllTokens] = useState([]);
  const [includeZeroOnly, setIncludeZeroOnly] = useState(false);
  const [tokenSearchQuery, setTokenSearchQuery] = useState('');


  const fetchWalletData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await hiveService.getWalletInfo(username);
      setWalletData(data);

      // Fetch all engine tokens (including zero-only) for optional display
      try {
        const engineAll = await hiveService.getAllHiveEngineTokens(username);
        setEngineAllTokens(engineAll || []);
      } catch (e) {
        console.warn('Failed to fetch all engine tokens:', e.message || e);
        setEngineAllTokens([]);
      }

    } catch (err) {
      console.error('Error fetching wallet data:', err);
      setError('Failed to load wallet information');
    } finally {
      setLoading(false);
    }
  }, [username]);

  useEffect(() => {
    fetchWalletData();
  }, [fetchWalletData]);

  // Disable background scrolling while popup is open
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previousOverflow || ''; };
  }, []);

  // If the user enables "Include zero-only tokens" and we don't have engineAllTokens yet, fetch them
  useEffect(() => {
    let mounted = true;
    const loadAll = async () => {
      if (includeZeroOnly && (!engineAllTokens || engineAllTokens.length === 0)) {
        try {
          const all = await hiveService.getAllHiveEngineTokens(username);
          if (mounted) setEngineAllTokens(all || []);
        } catch (err) {
          console.warn('Failed to fetch all engine tokens on toggle:', err.message || err);
        }
      }
    };

    loadAll();
    return () => { mounted = false; };
  }, [includeZeroOnly, username, engineAllTokens]);

  // Open input when clicking Fetch Totals
  const fetchTotals = () => {
    setShowTickerInput(true);
    setInputError('');
    setShowTotals(false);
  };

  const handleGetTotals = () => {
    if (!walletData) return;
    const raw = tickerInput || '';
    const tickers = raw.split(',').map(t => t.trim().replace(/\$/g, '').toUpperCase()).filter(t => t.length > 0);

    if (tickers.length === 0) {
      setInputError('Please enter at least one token ticker');
      return;
    }

    const totals = {};
    tickers.forEach(ticker => {
      // Special handling for HP - look for HIVE token directly
      if (ticker === 'HP') {
        const hiveToken = (walletData.tokens || []).find(tok => (tok.ticker || '').toUpperCase() === 'HIVE');
        if (!hiveToken) {
          totals[ticker] = null;
        } else {
          const totalHP = parseFloat(hiveToken.total_hp || 0);
          totals[ticker] = `${formatNumber(totalHP)}`;
        }
        return;
      }

      const token = (walletData.tokens || []).find(tok => tok.ticker && tok.ticker.toUpperCase() === ticker);
      if (!token) {
        totals[ticker] = null; // not found
      } else if (ticker === 'HIVE') {
          // HIVE: include liquid + savings; optionally include SWAP.HIVE when delegated toggle is on
          const liquidVal = parseFloat(token.liquid || 0);
          const savingsVal = parseFloat(token.savings || 0);

          if (!includeDelegated) {
            const combined = liquidVal + savingsVal;
            totals[ticker] = `${formatNumber(combined)}`;
          } else {
            // Include liquid SWAP.HIVE in the combined value
            let swapHiveVal = 0;
            const swapHiveToken = (walletData.tokens || []).find(t => (t.ticker || '').toUpperCase() === 'SWAP.HIVE');
            if (swapHiveToken) {
              swapHiveVal = parseFloat(swapHiveToken.liquid || 0);
            }
            const combined = liquidVal + savingsVal + swapHiveVal;
            totals[ticker] = `${formatNumber(combined)}`;
          }
        } else if (ticker === 'HBD') {
          // HBD: include liquid + savings; optionally include SWAP.HBD when delegated toggle is on
          const liquidVal = parseFloat(token.liquid || 0);
          const savingsVal = parseFloat(token.savings || 0);

          if (!includeDelegated) {
            const combined = liquidVal + savingsVal;
            totals[ticker] = `${formatNumber(combined)}`;
          } else {
            // Include liquid SWAP.HBD in the combined value
            let swapHbdVal = 0;
            const swapHbdToken = (walletData.tokens || []).find(t => (t.ticker || '').toUpperCase() === 'SWAP.HBD');
            if (swapHbdToken) {
              swapHbdVal = parseFloat(swapHbdToken.liquid || 0);
            }
            const combined = liquidVal + savingsVal + swapHbdVal;
            totals[ticker] = `${formatNumber(combined)}`;
          }
        } else {
          // Other tokens: include liquid + staked + savings; optionally include delegated-out
          const liquidVal = parseFloat(token.liquid || 0);
          const stakedVal = parseFloat(token.staked || 0);
          const savingsVal = parseFloat(token.savings || 0);

          // get delegated-out value (try token fields first, then fallback to engineAllTokens lookup)
          let delegatedVal = parseFloat(token.delegated || token.delegationsOut || 0) || 0;
          if (delegatedVal === 0 && engineAllTokens && engineAllTokens.length) {
            const eng = engineAllTokens.find(t => (t.ticker || '').toUpperCase() === ticker);
            if (eng) delegatedVal = parseFloat(eng.delegationsOut || eng.delegated || 0) || delegatedVal;
          }

          let totalValue;
          if (!includeDelegated) {
            totalValue = liquidVal + stakedVal + savingsVal;
          } else {
            // include staked and delegated-out when "Include Delegated in Totals" is checked
            totalValue = liquidVal + stakedVal + savingsVal + delegatedVal;
          }

          totals[ticker] = formatNumber(totalValue);
        }
    });

    setTotalsData(totals);
    setShowTotals(true);
    setShowTickerInput(false);
  };

  const formatNumber = (value) => {
    const num = parseFloat(value || 0);
    if (!isFinite(num)) return '0,00';
    // Truncate (round down) to 2 decimals
    const sign = num < 0 ? -1 : 1;
    const absFloored = Math.floor(Math.abs(num) * 100) / 100;
    const floored = sign * absFloored;

    // Format with '.' as thousand separator and ',' as decimal separator by using 'de-DE' locale
    return floored.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  if (loading) {
    return (
      <div className="wallet-popup">
        <div className="wallet-content">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="wallet-popup">
        <div className="wallet-content">
          <div className="wallet-header-fixed">
            <h2>Wallet Information</h2>
            <button className="close-button" onClick={onClose}>×</button>
          </div>
          <div className="error-message">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="wallet-popup" onClick={onClose}>
      <div className="wallet-content" onClick={(e) => e.stopPropagation()}>
        <div className="wallet-header-fixed">
          <div className="wallet-header-title">
            <h2>Wallet Information - @{username}</h2>
            <div className="header-controls">
              <label className="include-delegated-label">
                <input 
                  type="checkbox" 
                  checked={includeDelegated}
                  onChange={(e) => setIncludeDelegated(e.target.checked)}
                  className="include-delegated-checkbox"
                />
                Include Delegated in Totals
              </label>

              <div className="fetch-input-container">
                {!showTickerInput ? (
                  <button 
                    className="fetch-totals-button" 
                    onClick={fetchTotals}
                  >
                    Fetch Totals
                  </button>
                ) : (
                  <div className="fetch-input">
                    <input
                      type="text"
                      placeholder="Enter tickers, e.g. HIVE, LEO"
                      value={tickerInput}
                      onChange={(e) => setTickerInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleGetTotals(); }}
                      className="ticker-input"
                    />
                    <button className="get-totals-button" onClick={handleGetTotals}>Get Totals</button>
                    <button className="cancel-input" onClick={() => { setShowTickerInput(false); setTickerInput(''); setInputError(''); }}>Cancel</button>
                  </div>
                )}
                {inputError && <div className="input-error">{inputError}</div>}
              </div>

            </div>
          </div>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="wallet-body">
          {showTotals && totalsData && (
            <div className="totals-section">
              <h3>Totals Overview</h3>
              <table className="totals-table">
                <thead>
                  <tr>
                    <th>Token Ticker</th>
                    <th>Total Value (Liquid + Staked + Savings{includeDelegated ? ' + Delegated' : ''})</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(totalsData).map(([ticker, total]) => (
                    <tr key={ticker}>
                      <td>{ticker}</td>
                      <td>{total}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="hivepower-section">
            <h3>HivePower</h3>
            <div className="table-container">
              <table className="wallet-table hivepower-table">
                <thead>
                  <tr>
                    <th>Token Ticker (HP)</th>
                    <th>Staked Hive</th>
                    <th>Delegated HIVE (in - out)</th>
                    <th>Savings</th>
                    <th>Total Useable</th>
                  </tr>
                </thead>
                <tbody>
                  {walletData && (() => {
                    const hiveToken = (walletData.tokens || []).find(t => (t.ticker || '').toUpperCase() === 'HIVE');
                    if (!hiveToken) return (
                      <tr><td colSpan={5}>No HIVE data available</td></tr>
                    );

                    const totalHP = parseFloat(hiveToken.total_hp || 0);
                    const delegatedOut = parseFloat(hiveToken.delegated || 0);
                    const delegatedIn = parseFloat(hiveToken.received || 0);
                    const savings = parseFloat(hiveToken.savings || 0);

                    // Total useable as requested: (total_hp + received) - delegated
                    const totalUseable = totalHP + delegatedIn - delegatedOut;

                    // Debug log for these computed values
                    /* eslint-disable no-console */
                    console.log('DEBUG Wallet HIVE values (updated):', {
                      totalHP,
                      delegatedIn,
                      delegatedOut,
                      totalUseable,
                      hiveToken,
                      dynamic_props: walletData.dynamic_props,
                    });
                    /* eslint-enable no-console */

                    return (
                        <>
                        <tr>
                          <td className="ticker-cell">HivePower (HP) - Staked HIVE</td>
                          <td>{`${formatNumber(totalHP)} HP`}</td>
                          <td>{`${formatNumber(delegatedIn)} / ${formatNumber(delegatedOut)}`}</td>
                          <td>{formatNumber(savings)}</td>
                          <td>{`${formatNumber(totalUseable)} HP`}</td>
                        </tr>


                      </>
                    );
                  })()}
                </tbody>
              </table>
            </div>

            <div className="hive-raw-controls">
              <button
                className="raw-toggle"
                onClick={() => setShowHiveRaw(prev => !prev)}
              >
                {showHiveRaw ? 'Hide raw HIVE data' : 'Show raw HIVE data'}
              </button>

              {/*showHiveRaw && (
                <pre className="raw-data-block">{JSON.stringify((walletData.tokens || []).find(t => (t.ticker || '').toUpperCase() === 'HIVE') || {}, null, 2)}</pre>
              )*/}
            </div>
          </div>

          <div className="detailed-section">
            <div className="detailed-header">
              <h3>Detailed Breakdown</h3>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  type="text"
                  placeholder="Search ticker..."
                  value={tokenSearchQuery}
                  onChange={(e) => setTokenSearchQuery(e.target.value)}
                  className="ticker-search-input"
                />
                <label className="include-zero-label">
                  <input
                    type="checkbox"
                    className="include-zero-checkbox"
                    checked={includeZeroOnly}
                    onChange={(e) => setIncludeZeroOnly(e.target.checked)}
                  />
                  Include zero-only tokens
                </label>

                {/*<button
                  className="raw-toggle"
                  onClick={() => setShowEngineRaw(prev => !prev)}
                >
                  {showEngineRaw ? 'Hide raw engine data' : 'Show raw engine data'}
                </button>*/}


              </div>
            </div>

            {showEngineRaw && (
              <pre className="raw-data-block">{JSON.stringify(
                includeZeroOnly
                  ? (engineAllTokens || []).filter(t => (t.ticker || '').toUpperCase() !== 'HIVE')
                  : (walletData.tokens || []).filter(t => (t.ticker || '').toUpperCase() !== 'HIVE')
              , null, 2)}</pre>
            )}



            <div className="table-container">
              <table className="wallet-table">
                <thead>
                  <tr>
                    <th>Token Ticker</th>
                    <th>Liquid Assets</th>
                    <th>Staked (HP for HIVE)</th>
                    <th>Savings</th>
                    <th>Total Assets</th>
                  </tr>
                </thead>
                <tbody>
                  {walletData && walletData.tokens && walletData.tokens
                    .filter(token => tokenSearchQuery.length >= 2 ? (token.ticker || '').toLowerCase().includes(tokenSearchQuery.toLowerCase()) : true)
                    .map((token, index) => {
                    const isHive = (token.ticker || '').toUpperCase() === 'HIVE';
                    // prefer token.delegated, fallback to 'delegations' or lookup in engineAllTokens
                    let delegatedVal = parseFloat(token.delegated || token.delegations || 0) || 0;
                    if (delegatedVal === 0 && engineAllTokens && engineAllTokens.length) {
                      const eng = engineAllTokens.find(t => (t.ticker || '').toUpperCase() === (token.ticker || '').toUpperCase());
                      if (eng) delegatedVal = parseFloat(eng.delegated || eng.delegations || 0) || delegatedVal;
                    }

                    // Total assets: for HIVE, don't add HP to HIVE total; show HIVE liquid + savings
                    // Compute totals
                    const savingsVal = parseFloat(token.savings || 0);
                    const liquidVal = parseFloat(token.liquid || 0);
                    const stakedVal = parseFloat(token.staked || 0);
                    // delegatedVal is computed above (may come from engineAllTokens fallback)

                    let totalAssets;
                    let stakedDisplay = '';

                    if (isHive) {
                      // For HIVE: total assets (in this table) = liquid + savings
                      totalAssets = liquidVal + savingsVal;

                      // Show total_hp and delegated out in parentheses as requested
                      const totalHPVal = parseFloat(token.total_hp || 0);
                      stakedDisplay = `${formatNumber(totalHPVal)} HP${delegatedVal > 0 ? ` (${formatNumber(delegatedVal)} delegated)` : ''}`;
                    } else {
                      // For Hive Engine tokens: include delegated in total assets
                      totalAssets = liquidVal + stakedVal + savingsVal + delegatedVal;

                      stakedDisplay = delegatedVal > 0
                        ? `${formatNumber(stakedVal)} (${`delegated: ${formatNumber(delegatedVal)}`})`
                        : formatNumber(stakedVal);
                    }

                    return (
                      <tr key={index}>
                        <td className="ticker-cell">{token.ticker}</td>
                        <td>{formatNumber(liquidVal)}</td>
                        <td>{stakedDisplay}</td>
                        <td>{formatNumber(savingsVal)}</td>
                        <td>{formatNumber(totalAssets)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>


        </div>
      </div>
    </div>
  );
};

export default WalletInfo;
